import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import type { TaskAttachment } from '@/types/task';

export const useTaskAttachments = (taskId: string | null) => {
  const { user, company } = useAuth();
  const queryClient = useQueryClient();

  const attachmentsQuery = useQuery({
    queryKey: ['task-attachments', taskId],
    queryFn: async () => {
      if (!taskId) return [];
      const { data, error } = await supabase
        .from('task_attachments')
        .select('*')
        .eq('task_id', taskId)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return (data ?? []) as TaskAttachment[];
    },
    enabled: !!taskId && !!company?.id,
  });

  const uploadMutation = useMutation({
    mutationFn: async (file: File) => {
      const companyId = company?.id;
      const userId = user?.id;
      if (!taskId || !companyId || !userId) throw new Error('Missing context');

      // Unique path using crypto (never Math.random)
      const randomBytes = crypto.getRandomValues(new Uint8Array(8));
      const randomId = Array.from(randomBytes)
        .map(b => b.toString(16).padStart(2, '0')).join('');
      const ext = file.name.split('.').pop() ?? 'bin';
      const storagePath = `${companyId}/${taskId}/${Date.now()}-${randomId}.${ext}`;

      const { error: uploadError } = await supabase.storage
        .from('task-attachments')
        .upload(storagePath, file, { contentType: file.type });
      if (uploadError) throw uploadError;

      const { data, error } = await supabase
        .from('task_attachments')
        .insert({
          task_id: taskId,
          company_id: companyId,
          uploaded_by: userId,
          file_name: file.name,
          storage_path: storagePath,
          file_size: file.size,
          file_type: file.type,
        })
        .select()
        .single();
      if (error) throw error;
      return data as TaskAttachment;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['task-attachments', taskId] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (params: { attachmentId: string; storagePath: string }) => {
      // Remove from storage (best-effort — does not fail if file already absent)
      await supabase.storage.from('task-attachments').remove([params.storagePath]);
      const { error } = await supabase
        .from('task_attachments')
        .delete()
        .eq('id', params.attachmentId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['task-attachments', taskId] });
    },
  });

  return {
    attachments: attachmentsQuery.data ?? [],
    isLoading: attachmentsQuery.isLoading,
    uploadAttachment: uploadMutation.mutateAsync,
    isUploading: uploadMutation.isPending,
    deleteAttachment: deleteMutation.mutateAsync,
    isDeleting: deleteMutation.isPending,
  };
};
